import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PROVIDERS } from "../../../utils";

// Import icons
import fire from "../../../images/fire.svg";
import crash from "../../../images/crash.svg";
import virtuals from "../../../images/virtuals.svg";
import lite from "../../../images/lite.svg";
import drops from "../../../images/drops.svg";
import slots from "../../../images/slots.svg";
import table from "../../../images/table.svg";
import live from "../../../images/live.svg";
import split from "../../../images/split.svg";
import top from "../../../images/top.svg";
import newIcon from "../../../images/new.svg";
import { useGetCategoriesQuery } from "../../../store/apis";

const MobileLeftPart = ({
  selectedProvider,
  setSelectedProvider,
  activeCategory,
  onCategoryChange,
}) => {
  // Track whether we're showing categories or providers
  const [showingType, setShowingType] = useState("categories");

  // Get categories from API
  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetCategoriesQuery();

  // Default categories
  const [categories, setCategories] = useState([
    { id: 1, linktext: "/", name: "All", image: fire, count: null },
    { id: 2, linktext: "/", name: "Top", image: top, count: null },
    { id: 3, linktext: "/", name: "New", image: newIcon, count: null },
    { id: 4, linktext: "/", name: "PragmaticLive", image: live, count: 0 },
    { id: 5, linktext: "/", name: "Crash", image: crash, count: 0 },
    { id: 6, linktext: "/", name: "Virtuals", image: virtuals, count: 0 },
    { id: 7, linktext: "/", name: "Lite", image: lite, count: 0 },
    { id: 8, linktext: "/", name: "Drops and …", image: drops, count: 0 },
    { id: 9, linktext: "/", name: "Slots", image: slots, count: 0 },
    { id: 10, linktext: "/", name: "Table/Cards", image: table, count: 0 },
  ]);

  // Update categories with counts from API
  useEffect(() => {
    if (categoriesData && Array.isArray(categoriesData)) {
      setCategories((prevCategories) => {
        return prevCategories.map((category) => {
          // Try to find matching category in the API data (case insensitive)
          const matchingCategory = categoriesData.find(
            (apiCat) =>
              apiCat.category?.toLowerCase() === category.name.toLowerCase()
          );

          // If found, update the count
          if (matchingCategory) {
            return {
              ...category,
              count: matchingCategory.game_count || 0,
            };
          }

          // Special case for "All" - sum all game counts
          if (category.name === "All") {
            const totalCount = categoriesData.reduce(
              (sum, apiCat) => sum + (apiCat.game_count || 0),
              0
            );
            return {
              ...category,
              count: totalCount,
            };
          }

          return category;
        });
      });
    }
  }, [categoriesData]);

  // Handle category click
  const handleCategoryClick = (e, categoryName) => {
    e.preventDefault();

    // If selecting a category, clear any selected provider
    if (selectedProvider) {
      setSelectedProvider(null);
    }

    // Update the active category
    onCategoryChange(categoryName);
  };

  // Handle provider click
  const handleProviderClick = (e, provider) => {
    e.preventDefault();

    // If selecting a provider, clear active category
    if (activeCategory) {
      onCategoryChange(null);
    }

    // Update the selected provider
    setSelectedProvider(provider);
  };

  return (
    <div className="block md:hidden bg-white dark:bg-bluedarkmode my-[-10px]">
      {/* Tab switcher for Categories/Providers */}
      <div className="flex border-b border-darkblue">
        <button
          onClick={() => setShowingType("categories")}
          className={`flex-1 py-2 text-center text-sm font-medium 
            ${
              showingType === "categories"
                ? "text-yellow border-b-2 border-yellow"
                : "text-darkblue dark:text-white/60"
            }`}
        >
          Categories
        </button>
        <button
          onClick={() => setShowingType("providers")}
          className={`flex-1 py-2 text-center text-sm font-medium
            ${
              showingType === "providers"
                ? "text-yellow border-b-2 border-yellow"
                : "text-darkblue dark:text-white/60"
            }`}
        >
          Providers
        </button>
      </div>

      {/* Categories scrollable section */}
      {showingType === "categories" && (
        <div className="flex gap-2 scrollable border-b border-bluedarkmode overflow-x-auto">
          {categories.map((category) => (
            <Link
              to={category.linktext}
              key={category.id}
              onClick={(e) => handleCategoryClick(e, category.name)}
              className={`bg-transparent p-2 transition-all duration-300 group border-b-2 border-transparent ${
                activeCategory === category.name
                  ? "border-yellow !bg-black/10 dark:!bg-darktitleColor"
                  : ""
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <span className="w-3 min-w-3 max-w-3 s15:w-4 s15:min-w-4 s15:max-w-4">
                  {category.image && (
                    <img
                      src={category.image}
                      alt={category.name}
                      className="object-contain w-4 h-4 dark:invert"
                    />
                  )}
                </span>
                <p className="text-xs s15:text-sm whitespace-nowrap font-normal !leading-none text-darkblue dark:text-white/60 capitalize">
                  {category.name.length > 6
                    ? `${category.name.slice(0, 6)}...`
                    : category.name}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Providers scrollable section */}
      {showingType === "providers" && (
        <div className="flex gap-2 scrollable border-b border-darkblue overflow-x-auto">
          {Object.values(PROVIDERS).map((provider) => (
            <Link
              to="/"
              key={provider.id}
              onClick={(e) => handleProviderClick(e, provider)}
              className={`bg-transparent p-2 transition-all duration-300 group border-b-2 border-transparent ${
                selectedProvider?.id === provider.id
                  ? "border-yellow !bg-black/10 dark:!bg-darktitleColor"
                  : ""
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <span className="w-3 min-w-3 max-w-3 s15:w-4 s15:min-w-4 s15:max-w-4">
                  <img
                    src={provider.icon}
                    alt={provider.name}
                    className="object-contain w-4 h-4 dark:invert"
                  />
                </span>
                <p className="text-xs s15:text-sm whitespace-nowrap font-normal !leading-none text-darkblue dark:text-white/60 capitalize">
                  {provider.name.length > 6
                    ? `${provider.name.slice(0, 6)}...`
                    : provider.name}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileLeftPart;
